import { Loader2, X } from "lucide-react";
import type React from "react";
import type { GraphBuildJob } from "@/api/graphBuilderApi";

interface SourceJobStatusProps {
	currentJobId: string | null;
	jobStatus: GraphBuildJob | undefined;
	onClearJob: () => void;
}

const SourceJobStatus: React.FC<SourceJobStatusProps> = ({ currentJobId, jobStatus, onClearJob }) => {
	if (!currentJobId || !jobStatus) {
		return null;
	}

	const statusLabel =
		jobStatus.status === "formalizing"
			? "Converting to formal logic..."
			: jobStatus.status === "building"
				? "Building knowledge graph..."
				: jobStatus.status === "loading"
					? "Loading documents..."
					: jobStatus.status === "completed"
						? "Completed"
						: jobStatus.status === "failed"
							? "Failed"
							: jobStatus.status;

	return (
		<div className="surface-muted border border-strong rounded-lg p-3 space-y-2">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					{jobStatus.status === "completed" ? (
						<div className="w-2 h-2 bg-green-500 rounded-full" />
					) : jobStatus.status === "failed" ? (
						<div className="w-2 h-2 bg-red-500 rounded-full" />
					) : (
						<Loader2 className="w-3 h-3 text-primary-600 animate-spin" />
					)}
					<span className="text-xs font-medium text-muted capitalize">{statusLabel}</span>
				</div>
				<button
					type="button"
					onClick={onClearJob}
					className="p-1 soft-hover rounded transition-colors"
					aria-label="Clear status"
				>
					<X className="w-3 h-3 text-subtle" />
				</button>
			</div>
			{jobStatus.progress !== undefined && jobStatus.progress < 100 && (
				<div className="space-y-1">
					<div className="progress-fill h-1.5">
						<progress max={100} value={Math.max(0, Math.min(100, jobStatus.progress))} />
					</div>
					<p className="text-xs text-subtle text-right">{jobStatus.progress}%</p>
				</div>
			)}
			{jobStatus.error && <p className="text-xs text-red-600 mt-1">{jobStatus.error}</p>}
			{jobStatus.status === "completed" && jobStatus.graphId && (
				<p className="text-xs text-green-600 mt-1">Graph ready! Loading...</p>
			)}
		</div>
	);
};

export default SourceJobStatus;
